import { motion } from "framer-motion";
import { 
  Heart, Sun, Camera, Sparkles, 
  MapPin, Users, Star, Check 
} from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/lg6ur0og_WhatsApp%20Image%202026-02-28%20at%2014.45.46%20%281%29.jpeg",
  interior: "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/dlpsv85t_WhatsApp%20Image%202026-02-28%20at%2014.15.29.jpeg",
  deck: "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/wexjlsok_WhatsApp%20Image%202026-04-05%20at%2015.03.12.jpeg",
  kitchen: "https://customer-assets.emergentagent.com/job_9aafd9a9-bdf8-4d35-9ad3-a45b89c1eed6/artifacts/uy0wqghj_WhatsApp%20Image%202026-04-05%20at%2015.29.22.jpeg",
};

const HeroSection = () => (
  <section className="relative h-[80vh] min-h-[600px]" data-testid="wedding-hero">
    <img
      src={IMAGES.hero}
      alt="Bridal setting at Weaver's Nest"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
    <div className="absolute inset-0 flex items-center">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl"
        >
          <p className="text-sm uppercase tracking-[0.3em] text-white/80 mb-4 flex items-center gap-2">
            <Heart className="w-4 h-4" /> For Brides & Bridal Parties
          </p>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-6 leading-tight">
            A Beautiful Space for Wedding Mornings
          </h1>
          <p className="text-xl text-white/90 leading-relaxed mb-8">
            Calm. Private. Light-filled. Just minutes from Port Elizabeth's top wedding venues.
          </p>
          <a 
            href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20am%20interested%20in%20booking%20for%20a%20bridal%20party%20at%20Weaver%27s%20Nest%20Cottage" 
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wedding-hero-book"
          >
            <Button className="bg-white text-brand-charcoal hover:bg-white/90 rounded-full px-10 py-6 text-lg font-medium">
              Book Your Bridal Stay
            </Button>
          </a>
        </motion.div>
      </div>
    </div>
  </section>
);

const WhyBridesLoveSection = () => {
  const reasons = [
    {
      icon: Sun,
      title: "Natural Light Paradise",
      description: "Large windows flood every space with soft, flattering light — perfect for getting-ready photos."
    },
    {
      icon: Camera,
      title: "Photography Friendly",
      description: "Natural textures, greenery, and beautiful backdrops make every corner camera-ready."
    },
    {
      icon: Sparkles,
      title: "Spacious Prep Areas",
      description: "Generous rooms for hair, makeup, and styling with your bridal party."
    },
    {
      icon: MapPin,
      title: "Close to PE Venues",
      description: "Just minutes from The Plantation, The Boma, and other top wedding venues."
    },
    {
      icon: Users,
      title: "Private & Intimate",
      description: "No crowds, no distractions — just calm, beauty, and your closest people."
    },
    {
      icon: Heart,
      title: "Romantic Setting",
      description: "The perfect backdrop for a slow, special wedding morning surrounded by nature."
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-white" data-testid="wedding-reasons">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="section-subheading">Why Brides Choose Us</p>
          <h2 className="section-heading">Why Brides Love Weaver's Nest</h2>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-brand-cream p-8 rounded-2xl"
            >
              <div className="w-14 h-14 rounded-full bg-brand-sage/10 flex items-center justify-center mb-6">
                <reason.icon className="w-7 h-7 text-brand-sage" />
              </div>
              <h3 className="font-heading text-xl mb-3">{reason.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{reason.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const IdealForSection = () => {
  const ideals = [
    { icon: "☀️", label: "Wedding Mornings" },
    { icon: "📸", label: "Bridal Photography" },
    { icon: "🏡", label: "Wedding Guest Accommodation" },
  ];

  return (
    <section className="py-20 md:py-32 bg-brand-cream" data-testid="wedding-ideal">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="section-subheading">Perfect For</p>
            <h2 className="section-heading mb-8">Ideal For Your Wedding Plans</h2>
            
            <div className="grid grid-cols-2 gap-4">
              {ideals.map((ideal, index) => (
                <div
                  key={index}
                  className="bg-white p-4 rounded-xl flex items-center gap-3"
                >
                  <span className="text-2xl">{ideal.icon}</span>
                  <span className="font-medium text-brand-charcoal">{ideal.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="img-zoom rounded-2xl overflow-hidden">
              <img
                src={IMAGES.interior}
                alt="Bridal prep space"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
            <div className="img-zoom rounded-2xl overflow-hidden mt-8">
              <img
                src={IMAGES.deck}
                alt="Outdoor setting"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const TestimonialSection = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="wedding-testimonial">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex justify-center gap-1 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-6 h-6 fill-brand-sage text-brand-sage" />
            ))}
          </div>
          
          <blockquote className="font-accent text-2xl md:text-3xl lg:text-4xl text-brand-charcoal italic leading-relaxed mb-8">
            "I stayed at Weaver's Nest the night before my wedding and it was everything 
            I needed. Calm, light-filled, and completely private. It made my wedding morning 
            feel slow and special — and the photos were stunning."
          </blockquote>
          
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-full bg-brand-sage/20 flex items-center justify-center">
              <span className="font-heading text-lg text-brand-sage">S</span>
            </div>
            <div className="text-left">
              <p className="font-medium text-brand-charcoal">Sarah</p>
              <p className="text-sm text-muted-foreground">Bride, January 2026</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const PhotographySection = () => (
  <section className="py-20 md:py-32 bg-brand-charcoal text-white" data-testid="wedding-photography">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <Camera className="w-12 h-12 text-brand-sage mb-6" />
          <h2 className="font-heading text-4xl md:text-5xl mb-6">
            Bridal Photography Friendly
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            The property welcomes bridal photography. Natural textures, abundant 
            greenery, and soft natural light create the perfect conditions for 
            capturing intimate moments. Our quiet environment is ideal for those 
            special getting-ready shots.
          </p>
          <p className="text-brand-dusty-blue italic text-lg">
            Yes — photographers love it here too.
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="img-zoom rounded-2xl overflow-hidden">
            <img
              src={IMAGES.kitchen}
              alt="Light-filled interior"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white text-brand-charcoal p-6 rounded-xl shadow-lg max-w-xs">
            <p className="font-medium mb-2">Perfect Natural Light</p>
            <p className="text-sm text-muted-foreground">
              Large windows and open spaces provide ideal conditions for photography
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const HowToBookSection = () => (
  <section className="py-20 md:py-32 bg-brand-cream" data-testid="wedding-howto">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="section-subheading">Simple Steps</p>
        <h2 className="section-heading">How to Book a Bridal Stay</h2>
      </motion.div>
      
      <div className="max-w-3xl mx-auto">
        <div className="space-y-6">
          {[
            { step: "1", title: "Contact Us via WhatsApp", desc: "Send us a message with your wedding dates and requirements" },
            { step: "2", title: "Discuss Your Needs", desc: "We'll chat about your bridal party size and special requests" },
            { step: "3", title: "Confirm Availability", desc: "We'll check our calendar and confirm your dates" },
            { step: "4", title: "Book Direct & Save", desc: "Complete your booking and save on platform fees" },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex gap-6 items-start bg-white p-6 rounded-2xl"
            >
              <div className="w-12 h-12 rounded-full bg-brand-sage text-white flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-xl">{item.step}</span>
              </div>
              <div>
                <h3 className="font-heading text-xl mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="text-center mt-12">
          <a 
            href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20am%20interested%20in%20booking%20for%20a%20bridal%20party%20at%20Weaver%27s%20Nest%20Cottage" 
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wedding-book-btn"
          >
            <Button className="btn-primary text-lg px-10 py-6">
              Book Your Bridal Stay via WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const NearbyVenuesSection = () => (
  <section className="py-20 md:py-32 bg-white" data-testid="wedding-venues">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="section-subheading">Location</p>
        <h2 className="section-heading">Near PE's Top Wedding Venues</h2>
        <p className="text-muted-foreground text-lg mt-4 max-w-2xl mx-auto">
          Weaver's Nest is perfectly positioned near Port Elizabeth's most sought-after wedding venues.
        </p>
      </motion.div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {[
          { name: "The Plantation Wedding Venue", distance: "4.1 km" },
          { name: "The Boma Wedding Venue", distance: "3.6 km" },
          { name: "Sardinia Bay Beach", distance: "3.2 km" },
        ].map((venue, index) => (
          <div
            key={index}
            className="bg-brand-cream p-6 rounded-xl flex items-center gap-4"
          >
            <MapPin className="w-5 h-5 text-brand-sage flex-shrink-0" />
            <div>
              <p className="font-medium text-brand-charcoal">{venue.name}</p>
              <p className="text-sm text-muted-foreground">{venue.distance}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function WeddingsPage() {
  return (
    <div data-testid="weddings-page">
      <HeroSection />
      <WhyBridesLoveSection />
      <IdealForSection />
      <TestimonialSection />
      <PhotographySection />
      <HowToBookSection />
    </div>
  );
}
